/**
 * Savebar — sticky form footer with meta cells + secondary + primary actions.
 *
 *   <Savebar
 *     meta={[
 *       { k: 'Stav', v: 'Návrh — neuloženo', tone: 'warn' },
 *       { k: 'Esc', v: 'Zrušit', mono: true },
 *       { k: '⌘S', v: 'Uložit', mono: true },
 *     ]}
 *     actions={
 *       <>
 *         <Button mono size="lg">Zrušit</Button>
 *         <Button mono size="lg" variant="ink">Uložit a zavřít</Button>
 *       </>
 *     }
 *   />
 *
 * Source: .agents/design/project/artboard-radical-zakaznik-form.jsx:330-359
 */

import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

export interface SavebarMeta {
  k: ReactNode;
  v: ReactNode;
  /** Tint the value: `warn` for unsaved drafts, etc. */
  tone?: 'default' | 'warn' | 'ok';
}

interface SavebarProps {
  meta?: SavebarMeta[];
  /** Right-side action cluster — typically a pair of `<Button>`s. */
  actions: ReactNode;
  className?: string;
}

const TONE: Record<NonNullable<SavebarMeta['tone']>, string> = {
  default: 'text-ink',
  warn: 'text-warn',
  ok: 'text-ok',
};

export function Savebar({ meta, actions, className = '' }: SavebarProps) {
  // Phase 12.2: the Savebar slides up from below + fades in on mount so the
  // sticky footer reads as "appearing" rather than "always there". Consumers
  // that conditionally render the Savebar (dirty-form gate) get the slide-in
  // automatically; consumers that render it unconditionally still see the
  // slide on first page paint, which feels coherent with the modal scrim +
  // inspector dissolve. Wrap the consumer-side conditional in
  // `<AnimatePresence>` to also animate the exit when the bar disappears.
  const reduce = useReducedMotion();
  return (
    <motion.footer
      className={
        // Phone (14.3): tighter gutters; the 100dvh workspace cap keeps the
        // bar pinned above the soft keyboard's resized viewport.
        'sticky bottom-0 grid grid-cols-[1fr_auto] items-center gap-6 border-t border-line bg-paper px-8 py-4.5 max-sm:gap-3 max-sm:px-4 max-sm:py-3 ' +
        className
      }
      initial={reduce ? { y: 0, opacity: 1 } : { y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={reduce ? { y: 0, opacity: 0 } : { y: 24, opacity: 0 }}
      transition={{
        duration: reduce ? 0 : 0.24,
        ease: [0.2, 0, 0.13, 1],
      }}
    >
      {meta && meta.length > 0 ? (
        // Phone: keep the first meta cell (state indicator); keyboard-hint
        // cells don't apply to touch and would crowd the 390px bar.
        <dl className="flex gap-7 max-sm:gap-4 max-sm:[&>div:not(:first-child)]:hidden">
          {meta.map((m, i) => (
            <div key={i}>
              <dt className="mb-1 font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-3">
                {m.k}
              </dt>
              <dd
                className={
                  'font-mono text-[13px] tracking-[-0.01em] ' +
                  TONE[m.tone ?? 'default']
                }
              >
                {m.v}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <div />
      )}
      <div className="flex gap-3">{actions}</div>
    </motion.footer>
  );
}
